import { useEffect, useState } from 'react';
import { Descriptions, Form, Input, InputNumber, Modal, Select } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import MeterDetailsBlade from '../MeterDetailsBlade';
import BladeSection from '../BladeSection';
import styles from './AddCoPartyBlade.module.css';
import { CREATE_IPP_VENDOR } from '@graphql/vendorList.gql';
import { SAVE_COUNTER_PARTY_METER, VENDOR_CO_PARTY_INFO } from '@graphql/counterParty.gql';
import { IppVendor, CounterPartyType } from '@custom-types/vendor';

const { Option } = Select;

interface AddCoPartyBladeProps {
  open: boolean;
  onClose: () => void;
  primaryVendor: IppVendor | null;
  onSaved: () => void;
}

interface AddCoPartyFormValues {
  counterPartyType: CounterPartyType;
  vendorInvoicePrefix: string;
  percentage: number;
  glNumber?: string;
}

const readOnlyDate = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : null;

const AddCoPartyBlade = ({ open, onClose, primaryVendor, onSaved }: AddCoPartyBladeProps) => {
  const [form] = Form.useForm<AddCoPartyFormValues>();
  const [modal, contextHolder] = Modal.useModal();
  const [mounted, setMounted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [autoFilledPrefix, setAutoFilledPrefix] = useState<string | null>(null);

  const counterPartyType = Form.useWatch('counterPartyType', form);

  const [fetchCoPartyInfo, { data: coPartyInfoData, loading: coPartyInfoLoading }] = useLazyQuery(
    VENDOR_CO_PARTY_INFO,
    { fetchPolicy: 'network-only' }
  );
  const [createIppVendor] = useMutation(CREATE_IPP_VENDOR, { errorPolicy: 'all' });
  const [saveCounterPartyMeter] = useMutation(SAVE_COUNTER_PARTY_METER, { errorPolicy: 'all' });

  const coPartyInfo = coPartyInfoData?.vendorCoPartyInfo;

  useEffect(() => {
    if (open && primaryVendor) {
      setMounted(true);
      setInlineError(null);
      form.resetFields();
      // Start with the primary vendor's own prefix as-is; once a Counter
      // Party Type is picked, handleTypeChange appends it (see below).
      const initialPrefix = primaryVendor.vendorInvoicePrefix ?? '';
      form.setFieldsValue({ vendorInvoicePrefix: initialPrefix });
      setAutoFilledPrefix(initialPrefix);
      fetchCoPartyInfo({ variables: { vendorId: primaryVendor.id } });
    } else if (!open) {
      const timer = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open, primaryVendor, form, fetchCoPartyInfo]);

  if (!mounted || !primaryVendor) return null;

  const handleTypeChange = (type: CounterPartyType) => {
    const currentPrefix = form.getFieldValue('vendorInvoicePrefix');
    // Re-sync the prefix when it's empty or still matches what we last
    // auto-filled (i.e. the user hasn't manually edited it) — otherwise a
    // switch from CIB to NSPI (or back) would leave a stale, mismatched value.
    if (!currentPrefix || currentPrefix === autoFilledPrefix) {
      const suggested = `${primaryVendor.vendorInvoicePrefix ?? ''}${type}`;
      form.setFieldsValue({ vendorInvoicePrefix: suggested });
      setAutoFilledPrefix(suggested);
    }
  };

  const handleApply = async () => {
    setInlineError(null);

    if (coPartyInfo && !coPartyInfo.eligible) {
      setInlineError('This vendor must have exactly one meter before a Co-party can be added.');
      return;
    }
    if (coPartyInfo?.hasCoParty) {
      setInlineError('This vendor already has a Co-party assigned.');
      return;
    }

    let values: AddCoPartyFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const newVendorName = `${primaryVendor.vendorName} - ${values.counterPartyType}`;

    modal.confirm({
      title: 'Create this Co-party',
      content: (
        <>
          This will create a new vendor <strong>&quot;{newVendorName}&quot;</strong> with invoice prefix{' '}
          <strong>{values.vendorInvoicePrefix}</strong>, linked to meter{' '}
          <strong>{coPartyInfo?.meterNumber}</strong> at <strong>{values.percentage}%</strong>
        </>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      zIndex: 10002,
      onOk: () => doSave(values),
    });
  };

  const doSave = async (values: AddCoPartyFormValues) => {
    setSaving(true);
    try {
      const createResult = await createIppVendor({
        variables: {
          input: {
            vendorName: `${primaryVendor.vendorName} - ${values.counterPartyType}`,
            vendorContactNumber: primaryVendor.vendorContactNumber || undefined,
            vendorAddress: primaryVendor.vendorAddress || undefined,
            vendorEmailId: primaryVendor.vendorEmailId || undefined,
            vendorEmailId1: primaryVendor.vendorEmailId1 || undefined,
            vendorEmailId2: primaryVendor.vendorEmailId2 || undefined,
            vendorEmailId3: primaryVendor.vendorEmailId3 || undefined,
            purchaseOrderNo: primaryVendor.purchaseOrderNo || undefined,
            needDetailsWithInvoice: primaryVendor.needDetailsWithInvoice || undefined,
            commercialStatus: primaryVendor.commercialStatus || undefined,
            contactName: primaryVendor.contactName || undefined,
            departmentName: primaryVendor.departmentName || undefined,
            poCalendarYear: primaryVendor.poCalendarYear || undefined,
            vendorDueDate: primaryVendor.vendorDueDate || undefined,
            vendorInvoicePrefix: values.vendorInvoicePrefix,
            commercialOperationDate: primaryVendor.commercialOperationDate || undefined,
            statusDate: primaryVendor.statusDate || undefined,
            secondaryVendorType: values.counterPartyType,
          },
        },
      });

      if (createResult.errors?.length) {
        setInlineError(createResult.errors[0].message);
        return;
      }

      const newVendorId = createResult.data?.createIppVendor?.id;
      if (!newVendorId) {
        setInlineError('Failed to create Co-party vendor');
        return;
      }

      const linkResult = await saveCounterPartyMeter({
        variables: {
          ippId: newVendorId,
          meterNumber: coPartyInfo?.meterNumber,
          percentage: values.percentage,
          secondaryIppGl:
            values.counterPartyType === 'NSPI' && values.glNumber ? { glNumber: values.glNumber } : undefined,
        },
      });

      if (linkResult.errors?.length) {
        setInlineError(
          `Co-party vendor created, but linking it to the meter failed: ${linkResult.errors[0].message}`
        );
        return;
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      setInlineError(err instanceof Error ? err.message : 'Failed to save Co-party');
    } finally {
      setSaving(false);
    }
  };

  const displayVendorName = counterPartyType
    ? `${primaryVendor.vendorName} - ${counterPartyType}`
    : primaryVendor.vendorName;

  const invoiceDetailFields: Array<[string, string | number]> = (
    [
      ['Primary Invoice Email', primaryVendor.vendorEmailId],
      ['Additional Invoice Email 1', primaryVendor.vendorEmailId1],
      ['Additional Invoice Email 2', primaryVendor.vendorEmailId2],
      ['Additional Invoice Email 3', primaryVendor.vendorEmailId3],
      ['Purchase Order No', primaryVendor.purchaseOrderNo],
      ['PO Calendar Year', primaryVendor.poCalendarYear],
      ['Commercial Status', primaryVendor.commercialStatus],
      ['Vendor Monthly Due Date', primaryVendor.vendorDueDate],
      ['Date of Status', readOnlyDate(primaryVendor.statusDate)],
      ['Commercial Operation Date', readOnlyDate(primaryVendor.commercialOperationDate)],
    ] as Array<[string, string | number | null | undefined]>
  ).filter((entry): entry is [string, string | number] => entry[1] != null && entry[1] !== '');

  return (
    <>
    {contextHolder}
    <MeterDetailsBlade
      open={open}
      title="Add a new Co-party"
      onClose={onClose}
      monthLabel=""
      handleApply={handleApply}
      applyLabel="Save"
      saving={saving}
      isDirty
      inlineError={inlineError}
      infoSection={<></>}
    >
      <Form form={form} layout="vertical">
        <BladeSection>
          <div className={styles.card}>
            <Form.Item
              label="Counter Party Type"
              name="counterPartyType"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: 'Please select a Counter Party Type' }]}
            >
              <Select
                placeholder="Select CIB or NSPI"
                onChange={handleTypeChange}
                getPopupContainer={() => document.body}
                styles={{ popup: { root: { zIndex: 10002 } } }}
              >
                <Option value="CIB">CIB</Option>
                <Option value="NSPI">NSPI</Option>
              </Select>
            </Form.Item>
          </div>

          <div className={styles.card}>
            <div className={styles.groupLabel}>Vendor Name &amp; Contact Info</div>
            <div className={styles.readOnlyGroup}>
              <div className={styles.plainTextName}>{displayVendorName}</div>
              {primaryVendor.contactName && <div className={styles.plainText}>{primaryVendor.contactName}</div>}
              {primaryVendor.vendorAddress && <div className={styles.plainText}>{primaryVendor.vendorAddress}</div>}
              {primaryVendor.vendorContactNumber && (
                <div className={styles.plainTextMuted}>{primaryVendor.vendorContactNumber}</div>
              )}
              {primaryVendor.departmentName && (
                <div className={styles.plainTextMuted}>{primaryVendor.departmentName}</div>
              )}
            </div>
          </div>

          {invoiceDetailFields.length > 0 && (
            <div className={styles.card}>
              <div className={styles.groupLabel}>Invoice &amp; Status Details</div>
              <Descriptions bordered size="small" column={1}>
                {invoiceDetailFields.map(([label, value]) => (
                  <Descriptions.Item key={label} label={label}>
                    {value}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          )}

          <div className={styles.card}>
            <div className={`${styles.highlightedField}`}>
              <Form.Item
                label="Vendor Invoice Prefix"
                name="vendorInvoicePrefix"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Vendor Invoice Prefix is required' }]}
              >
                <Input placeholder="Enter Prefix (e.g., T1CIB)" maxLength={20} />
              </Form.Item>
            </div>

            <Form.Item label="Meter Number" style={{ marginBottom: 0, marginTop: 16 }}>
              <div className={styles.plainText}>
                {coPartyInfo?.meterNumber ?? (coPartyInfoLoading ? 'Loading…' : '—')}
              </div>
            </Form.Item>

            {coPartyInfo && !coPartyInfo.eligible && (
              <div className={styles.ineligibleNote}>
                This vendor doesn&apos;t have exactly one meter, so a Co-party can&apos;t be added.
              </div>
            )}
            {coPartyInfo?.hasCoParty && (
              <div className={styles.ineligibleNote}>
                This vendor already has a Co-party assigned ({coPartyInfo.coPartyVendorName}).
              </div>
            )}
          </div>
        </BladeSection>

        <BladeSection>
          <div className={styles.card}>
            <Form.Item
              label="What is the Payable to CIB Percentage?"
              name="percentage"
              rules={[
                { required: true, message: 'Please enter the percentage' },
                { type: 'number', min: 0.0001, max: 100, message: 'Must be between 0 and 100' },
              ]}
              style={{ marginBottom: counterPartyType === 'NSPI' ? 16 : 0 }}
            >
              <InputNumber
                min={0}
                max={100}
                step={0.0001}
                precision={4}
                placeholder="e.g., 25.5000"
                style={{ width: '100%' }}
              />
            </Form.Item>

            {counterPartyType === 'NSPI' && (
              <Form.Item label="Co-party GL" name="glNumber" style={{ marginBottom: 0 }}>
                <Input placeholder="Enter Co-party GL Number" />
              </Form.Item>
            )}
          </div>
        </BladeSection>
      </Form>
    </MeterDetailsBlade>
    </>
  );
};

export default AddCoPartyBlade;
